import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useReveal } from "@/hooks/useReveal";
import Marquee from "@/components/Marquee";
import { VytaText } from "@/components/Vyta";
import { IMG } from "@/lib/images";
import { SERVICES, TESTIMONIALS, SAAS } from "@/data/services";

const HERO_IMG = IMG.hero;
const DATA_VIS = IMG.towers;
const ABSTRACT_2 = IMG.office;

const METRICS = [
    { v: "+38%", l: "Aumento medio de pipeline en los dos primeros trimestres" },
    { v: "11 sem", l: "Tiempo medio hasta el primer sistema de ingresos en producción" },
    { v: "70%", l: "Del trabajo de operaciones comerciales automatizado en el segundo trimestre" },
    { v: "2.3 MUSD+", l: "Pipeline acumulado influenciado en nuestro portafolio" },
];

const WORK_STEPS = [
    {
        n: "01",
        t: "Diagnóstico",
        d: "Analizamos tu situación comercial, tus procesos y tus capacidades para detectar dónde está el valor.",
    },
    {
        n: "02",
        t: "Plan",
        d: "Definimos contigo prioridades, objetivos y un plan de ejecución con alcance y entregables claros.",
    },
    {
        n: "03",
        t: "Ejecución",
        d: "Implementamos el plan junto a tu equipo, con ritmo de trabajo y responsables concretos.",
    },
    {
        n: "04",
        t: "Seguimiento",
        d: "Medimos resultados, revisamos indicadores y ajustamos el plan de forma continua.",
    },
];

function AnimatedNumber({ value, duration = 1400 }) {
    const ref = useRef(null);
    const [display, setDisplay] = useState(value);
    useEffect(() => {
        // Pull leading sign / trailing symbols, animate digits
        const match = value.match(/^([^\d]*)([\d.,]+)(.*)$/);
        if (!match) return;
        const [, prefix, num, suffix] = match;
        const target = parseFloat(num.replace(/,/g, ""));
        if (Number.isNaN(target)) return;

        let started = false;
        const io = new IntersectionObserver(
            (ents) => {
                ents.forEach((e) => {
                    if (e.isIntersecting && !started) {
                        started = true;
                        const start = performance.now();
                        const tick = (t) => {
                            const p = Math.min(1, (t - start) / duration);
                            const eased = 1 - Math.pow(1 - p, 3);
                            const cur = target * eased;
                            const formatted =
                                num.includes(".") || num.includes(",")
                                    ? cur.toFixed(1)
                                    : Math.round(cur).toString();
                            setDisplay(`${prefix}${formatted}${suffix}`);
                            if (p < 1) requestAnimationFrame(tick);
                        };
                        requestAnimationFrame(tick);
                    }
                });
            },
            { threshold: 0.4 }
        );
        if (ref.current) io.observe(ref.current);
        return () => io.disconnect();
    }, [value, duration]);

    return (
        <span ref={ref} data-testid={`metric-number-${value}`}>
            {display}
        </span>
    );
}

export default function Home() {
    useReveal();
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="container-x pt-12 pb-24 lg:pt-20 lg:pb-40">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
                        <div className="lg:col-span-7 reveal">
                            <div className="label-mono flex items-center gap-3">
                                <span className="w-6 h-px bg-brand-electric" />
                                CONSULTORÍA · DIRECCIÓN · INTELIGENCIA ARTIFICIAL
                            </div>
                            <h1 className="mt-8 font-heading text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tighter font-medium text-brand-navy">
                                Estrategia, liderazgo e{" "}
                                <span className="italic text-brand-electric">inteligencia artificial</span>{" "}
                                para hacer crecer tu empresa.
                            </h1>
                            <p className="mt-8 max-w-xl text-lg text-brand-gray leading-relaxed">
                                En Scalexa Group combinamos consultoría, dirección
                                externa y ejecución comercial para ayudarte a captar
                                clientes, mejorar procesos y desarrollar nuevas
                                capacidades. Trabajamos con empresas B2B y B2C, con
                                especial foco en pymes industriales y tecnológicas.
                            </p>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <Link to="/contact" data-testid="hero-cta-strategy" className="btn-primary">
                                    Agenda una reunión <ArrowRight size={16} />
                                </Link>
                                <Link to="/services" data-testid="hero-cta-services" className="btn-ghost">
                                    Explora nuestros servicios <ArrowUpRight size={16} />
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5 reveal">
                            <div className="relative aspect-[4/5] w-full">
                                <img
                                    src={HERO_IMG}
                                    alt="Scalexa Group — consultoría empresarial, inteligencia artificial y aceleración comercial"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 border border-brand-line/80" />
                                <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.3em] text-white/80 mix-blend-difference">
                                    <span>SCX · 01 / 05</span>
                                    <span>2026, ∞</span>
                                </div>
                                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.3em] text-white/80 mix-blend-difference">
                                    <span>BLUEPRINT</span>
                                    <span>REV.ENG</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Marquee />

            {/* MANIFESTO */}
            <section className="border-b border-brand-line" data-testid="manifesto">
                <div className="container-x py-24 lg:py-32 reveal">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-3">
                            <div className="label-mono">Posicionamiento</div>
                        </div>
                        <div className="lg:col-span-9">
                            <h2 className="font-heading text-3xl sm:text-4xl lg:text-[56px] leading-[1.25] tracking-tight text-brand-navy space-y-3">
                                <span className="block">
                                    El criterio profesional dirige el trabajo.{" "}
                                    <span className="text-brand-gray/50">La inteligencia artificial</span>
                                </span>
                                <span className="block">
                                    <span className="italic text-brand-electric">amplifica las capacidades</span>{" "}
                                    del equipo.
                                </span>
                                <span className="block text-brand-gray/70">
                                    Experiencia humana, ejecución y tecnología.
                                </span>
                            </h2>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES — FIVE LINES */}
            <section className="container-x py-24 lg:py-32" data-testid="services-overview">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 reveal">
                    <div className="lg:col-span-4">
                        <div className="label-mono">Servicios</div>
                        <h2 className="font-heading text-4xl lg:text-5xl mt-6 leading-tight text-brand-navy">
                            Cinco líneas de servicio. <br /> Un mismo objetivo.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6">
                        <p className="text-brand-gray text-lg leading-relaxed">
                            Consultoría, dirección externa, desarrollo de negocio e
                            inteligencia artificial trabajando juntas. Cada línea
                            tiene su propia página: explora la que encaja con el
                            momento de tu empresa.
                        </p>
                    </div>
                </div>

                <div className="border-t border-brand-line">
                    {SERVICES.map((s) => (
                        <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            data-testid={`home-service-${s.slug}`}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 py-10 border-b border-brand-line group hover:bg-brand-soft transition-colors reveal"
                        >
                            <div className="lg:col-span-2 label-mono">{s.n}</div>
                            <div className="lg:col-span-4">
                                <h3 className="font-heading text-2xl lg:text-3xl text-brand-navy leading-tight">
                                    {s.name}
                                </h3>
                            </div>
                            <div className="lg:col-span-5">
                                <p className="text-brand-gray leading-relaxed">{s.short}</p>
                            </div>
                            <div className="lg:col-span-1 flex lg:justify-end items-center">
                                <ArrowUpRight
                                    className="text-brand-navy opacity-40 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                                    size={22}
                                />
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="mt-12 reveal">
                    <Link to="/services" data-testid="services-overview-cta" className="btn-secondary">
                        Explora nuestros servicios <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

            {/* CASOS Y EXPERIENCIA — RESULTADOS */}
            <section id="casos" className="bg-brand-navy text-white scroll-mt-24" data-testid="metrics-section">
                <div className="container-x py-24 lg:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
                        <div className="lg:col-span-5">
                            <div className="label-mono !text-white/60">Casos y experiencia · Resultados</div>
                            <h2 className="font-heading text-4xl lg:text-5xl mt-6 text-white leading-tight">
                                El trabajo se juzga <br />
                                por los <span className="italic">ingresos que mueve.</span>
                            </h2>
                            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
                                Cada colaboración se enmarca en una sola hipótesis de
                                ingresos. Estos son los promedios actualizados de cómo
                                se ve el crecimiento sostenido en nuestro portafolio.
                            </p>
                        </div>
                        <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-white/10">
                            {METRICS.map((m, i) => (
                                <div
                                    key={i}
                                    className="bg-brand-navy p-8 lg:p-10"
                                    data-testid={`metric-${i}`}
                                >
                                    <div className="font-heading text-5xl lg:text-6xl text-white tracking-tighter">
                                        <AnimatedNumber value={m.v} />
                                    </div>
                                    <div className="mt-4 text-white/60 text-sm leading-relaxed max-w-[16rem]">
                                        {m.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* TESTIMONIOS */}
            <section className="container-x py-24 lg:py-32" data-testid="testimonials">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 reveal">
                        <div className="label-mono">Testimonios</div>
                        <div className="mt-8 space-y-14">
                            {TESTIMONIALS.map((t, i) => (
                                <figure key={i} data-testid={`testimonial-${i}`}>
                                    <blockquote className="font-heading text-2xl lg:text-3xl leading-[1.25] text-brand-navy">
                                        "{t.q}"
                                    </blockquote>
                                    <figcaption className="mt-5 label-mono !text-brand-gray">
                                        <VytaText text={t.a} />
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-5 reveal">
                        <div className="relative aspect-square w-full">
                            <img
                                src={DATA_VIS}
                                alt="Resultados de proyectos de consultoría de Scalexa Group"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 border border-brand-line/80" />
                            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.3em] text-white/80">
                                <span>FIG · 03</span>
                                <span>SCX.RESEARCH</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CÓMO TRABAJAMOS */}
            <section className="border-t border-brand-line" data-testid="how-we-work">
                <div className="container-x py-24 lg:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 reveal">
                        <div className="lg:col-span-4">
                            <div className="label-mono">Cómo trabajamos</div>
                            <h2 className="font-heading text-4xl lg:text-5xl mt-6 leading-tight text-brand-navy">
                                Diagnóstico, plan, ejecución y seguimiento.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 lg:col-start-6">
                            <p className="text-brand-gray text-lg leading-relaxed">
                                Un método común a todas nuestras líneas de servicio:
                                entender antes de proponer, ejecutar con ritmo y
                                medir lo que importa.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-line border border-brand-line reveal">
                        {WORK_STEPS.map((s, i) => (
                            <div key={i} className="bg-white p-8 lg:p-10" data-testid={`work-step-${i}`}>
                                <div className="label-mono">{s.n}</div>
                                <h3 className="font-heading text-2xl mt-4 text-brand-navy">{s.t}</h3>
                                <p className="mt-3 text-sm text-brand-gray leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* EXPERIENCIA Y RED DE ESPECIALISTAS */}
            <section className="border-t border-brand-line" data-testid="experience">
                <div className="container-x py-24 lg:py-32">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-5 reveal">
                            <div className="label-mono">Experiencia y red de especialistas</div>
                            <h2 className="font-heading text-4xl lg:text-5xl mt-6 leading-tight text-brand-navy">
                                Experiencia humana.{" "}
                                <span className="italic text-brand-electric">Red especializada.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 space-y-12 reveal">
                            <div>
                                <h3 className="font-heading text-2xl text-brand-navy">Trayectoria</h3>
                                <p className="mt-4 text-brand-gray leading-relaxed">
                                    Los integrantes de Scalexa acumulan más de una
                                    década de experiencia en ventas complejas,
                                    industria, tecnología, dirección de equipos y
                                    desarrollo internacional.
                                </p>
                                <p className="mt-4 text-sm text-brand-gray/70 leading-relaxed">
                                    Trayectoria profesional previa de nuestros
                                    integrantes. Los resultados de los proyectos de
                                    Scalexa son los que figuran en la sección de
                                    casos y experiencia.
                                </p>
                            </div>
                            <div className="border-t border-brand-line pt-12">
                                <h3 className="font-heading text-2xl text-brand-navy">Red de especialistas</h3>
                                <p className="mt-4 text-brand-gray leading-relaxed">
                                    <VytaText
                                        text="Coordinamos una red de especialistas contrastados en transformación digital: VYTA Digital como socio tecnológico para el desarrollo e implementación de soluciones de IA, y colaboradores especializados en prospección B2B que ejecutan bajo la coordinación de Scalexa."
                                    />
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* DIAGNÓSTICO CTA */}
            <section
                className="relative border-y border-brand-line bg-brand-soft"
                data-testid="readiness-cta"
            >
                <div className="container-x py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7 reveal">
                            <div className="label-mono">Diagnóstico</div>
                            <h2 className="font-heading text-4xl lg:text-6xl mt-6 leading-[0.98] text-brand-navy">
                                ¿Dónde están <br />
                                las <span className="italic text-brand-electric">fugas en tus ingresos?</span>
                            </h2>
                            <p className="mt-6 text-brand-gray text-lg max-w-xl leading-relaxed">
                                Un diagnóstico de 90 segundos sobre tu motor
                                comercial y tu adopción de IA, entregado como un
                                informe ejecutivo de Scalexa: puntuación, nivel y
                                recomendaciones accionables.
                            </p>
                        </div>
                    <div className="lg:col-span-5 reveal">
                        <div className="bg-white border border-brand-line p-8 lg:p-10">
                            <div className="label-mono">Diagnóstico de crecimiento con IA</div>
                            <div className="mt-6 flex items-end gap-3">
                                <div className="font-heading text-7xl text-brand-navy">100</div>
                                <div className="text-brand-gray mb-3">/ 100 máx</div>
                            </div>
                            <div className="mt-6 h-1 bg-brand-line">
                                <div className="h-full w-[78%] bg-brand-electric" />
                            </div>
                            <div className="mt-4 flex items-center justify-between text-xs label-mono">
                                <span>CON FUGAS</span>
                                <span>CRECIENDO</span>
                            </div>
                            <Link
                                to="/assessment"
                                data-testid="readiness-cta-button"
                                className="btn-primary w-full mt-8"
                            >
                                Hacer el diagnóstico <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* SAAS — PRÓXIMAMENTE */}
            <section className="container-x py-24 lg:py-32" data-testid="saas-strip">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center reveal">
                    <div className="lg:col-span-8">
                        <div className="flex items-center gap-4">
                            <div className="label-mono">Producto</div>
                            <span className="label-mono !text-[10px] border border-brand-electric !text-brand-electric px-2.5 py-1">
                                {SAAS.badge}
                            </span>
                        </div>
                        <h2 className="font-heading text-4xl lg:text-5xl mt-6 leading-tight text-brand-navy">
                            {SAAS.name}
                        </h2>
                        <p className="mt-4 text-brand-gray text-lg max-w-xl leading-relaxed">
                            {SAAS.text}
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right flex lg:justify-end gap-4 flex-wrap">
                        <Link to="/saas" data-testid="saas-strip-link" className="btn-ghost">
                            Más información
                        </Link>
                        <Link
                            to="/contact?interes=saas"
                            data-testid="saas-strip-cta"
                            className="btn-primary"
                        >
                            {SAAS.cta} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* CLOSING */}
            <section className="container-x py-24 lg:py-32 border-t border-brand-line" data-testid="closing-cta">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end reveal">
                    <div className="lg:col-span-8">
                        <div className="label-mono">Contacto</div>
                        <h2 className="font-heading text-4xl lg:text-6xl mt-6 leading-[0.98] text-brand-navy">
                            Hablemos de tu próximo trimestre. <br />
                            <span className="italic text-brand-electric">Agenda una reunión.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <Link to="/contact" data-testid="closing-cta-contact" className="btn-primary">
                            Agenda una reunión <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
                <div className="mt-16">
                    <img
                        src={ABSTRACT_2}
                        alt="Scalexa Group — consultoría de estrategia comercial y transformación digital en Madrid"
                        className="w-full h-[320px] lg:h-[440px] object-cover border border-brand-line"
                    />
                </div>
            </section>
        </div>
    );
}
